import { useEffect, useState } from "react";
import { api, API, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Lightning, DownloadSimple } from "@phosphor-icons/react";

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

export default function Payroll() {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  async function load() {
    const { data } = await api.get(`/payroll?month=${month}&year=${year}`);
    setRows(data);
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month, year]);

  async function generate() {
    setBusy(true);
    try {
      const { data } = await api.post("/payroll/generate", { month, year });
      toast.success(`Generated ${data.count} payslips`);
      setRows(data.rows);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  }

  function download(slip) {
    window.open(`${API}/payroll/${slip.id}/pdf`, "_blank");
  }

  const total = rows.reduce((s, r) => s + Number(r.net_salary || 0), 0);

  return (
    <div className="p-6 md:p-10">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
        <div>
          <p className="text-xs tracking-[0.3em] uppercase text-[#E50914] mb-2">Engine</p>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tight">Payroll</h1>
          <p className="text-xs text-zinc-500 mt-1 font-mono">Net payout: ₹ {total.toLocaleString()}</p>
        </div>
        <div className="flex gap-3">
          <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
            <SelectTrigger className="w-32 bg-[#16161C] border-[#2D2D35] h-10" data-testid="payroll-month"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#16161C] border-[#2D2D35] text-white">
              {MONTHS.map((m, i) => <SelectItem key={i} value={String(i+1)}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="w-28 bg-[#16161C] border-[#2D2D35] h-10" data-testid="payroll-year"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#16161C] border-[#2D2D35] text-white">
              {[year-1, year, year+1].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={generate} disabled={busy} data-testid="payroll-generate"
                  className="bg-[#E50914] hover:bg-[#C80812] h-10">
            <Lightning size={16} className="mr-1" /> {busy ? "Computing…" : "Generate"}
          </Button>
        </div>
      </div>

      <div className="bg-[#16161C] border border-[#2D2D35] rounded-md overflow-x-auto">
        <table className="w-full min-w-[1100px]" data-testid="payroll-table">
          <thead>
            <tr className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-[#2D2D35]">
              <th className="text-left p-3">Employee</th>
              <th className="text-right p-3">Basic</th>
              <th className="text-right p-3">Present</th>
              <th className="text-right p-3">Absent</th>
              <th className="text-right p-3">Late (m)</th>
              <th className="text-right p-3">Att Ded</th>
              <th className="text-right p-3">Fines</th>
              <th className="text-right p-3">Adv Rec</th>
              <th className="text-right p-3">Incentives</th>
              <th className="text-right p-3">Net</th>
              <th className="text-right p-3"></th>
            </tr>
          </thead>
          <tbody className="font-mono text-sm">
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-[#2D2D35]/60 hover:bg-[#1C1C24]">
                <td className="p-3"><div className="text-white text-sm">{r.employee_name}</div><div className="text-[10px] text-zinc-500">{r.employee_code}</div></td>
                <td className="p-3 text-right">{Number(r.basic).toLocaleString()}</td>
                <td className="p-3 text-right text-[#22C55E]">{r.present_days}</td>
                <td className="p-3 text-right text-[#FF3B30]">{r.absent_days}</td>
                <td className="p-3 text-right text-[#F59E0B]">{r.late_minutes}</td>
                <td className="p-3 text-right">{r.attendance_deduction}</td>
                <td className="p-3 text-right">{r.fines}</td>
                <td className="p-3 text-right">{r.advance_recovery}</td>
                <td className="p-3 text-right text-[#22C55E]">+{r.incentives}</td>
                <td className="p-3 text-right text-base font-semibold text-[#E50914]">₹ {Number(r.net_salary).toLocaleString()}</td>
                <td className="p-3 text-right">
                  <button onClick={()=>download(r)} className="text-zinc-400 hover:text-[#E50914] p-2" data-testid={`payslip-pdf-${r.employee_code}`}>
                    <DownloadSimple size={16} weight="bold" />
                  </button>
                </td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan="11" className="text-center text-zinc-500 p-8 text-sm">No payslips. Click Generate.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
